import {useMapsLibrary} from "@vis.gl/react-google-maps";
import {useEffect, useState} from "react";

type LatLngLiteral = google.maps.LatLngLiteral;

interface IUseDistanceBetweenParams<T> {
  coordinates: T[];
  setCoordinates: (coords: (T & { distance: number })[]) => void;
  center: LatLngLiteral | null;
  /**
   * Function to extract lat/lng from coordinate objects.
   * Return null if coordinates are missing or invalid.
   */
  getLatLng: (item: T) => LatLngLiteral | null;
}

/** Meters per mile */
const METERS_TO_MILES = 1609.34;

/**
 * Hook to calculate distance between a center point and a list of coordinates using Google Maps Geometry library.
 * Adds a `distance` field (in miles) to each coordinate object.
 * @param coordinates - Array of objects to calculate distance for
 * @param setCoordinates - Setter function to update coordinates with a distance field
 * @param center - The center point to calculate distances from
 * @param getLatLng - Function to extract lat/lng from each coordinate object
 * @returns {isCalculating} - Boolean indicating if distance calculation is in progress
 */
const useDistanceBetween = <T,>({
  coordinates,
  setCoordinates,
  center,
  getLatLng
}: IUseDistanceBetweenParams<T>): { isCalculating: boolean } => {
  const geometryLibrary = useMapsLibrary('geometry');
  const [isCalculating, setIsCalculating] = useState(true);

  useEffect(() => {
    if (!geometryLibrary || !center || coordinates.length === 0) {
      setIsCalculating(false);
      return;
    }
    setIsCalculating(true);
    const {spherical} = geometryLibrary;

    // Mutate objects in place instead of creating new array
    coordinates.forEach((item: T) => {
      const latLng = getLatLng(item);

      // Guard against missing lat/lng in cached data
      if (
        !latLng ||
        !Number.isFinite(latLng.lat) ||
        !Number.isFinite(latLng.lng)
      ) {
        (item as T & { distance: number }).distance = Infinity;
      } else {
        const distanceMeters = spherical.computeDistanceBetween(center, latLng);
        const distanceMiles = distanceMeters / METERS_TO_MILES;
        // Round to nearest hundredth (2 decimal places)
        (item as T & { distance: number }).distance = Math.round(distanceMiles * 100) / 100;
      }
    });

    // Call setter to trigger re-render, but pass same array reference
    setCoordinates(coordinates as (T & { distance: number })[]);
    setIsCalculating(false);
  }, [geometryLibrary, coordinates, setCoordinates, center, getLatLng]);

  return {isCalculating};
}

export {
  useDistanceBetween
};